import {
    get,
    isArray,
    isString,
    isObject,
    isNumber,
    isBoolean,
    cloneDeep,
    sum,
    forOwn,
    includes,
} from "lodash";
import { isUndefined } from "util";
import qs from "qs";
import { fetchDataPromise } from ".";
import { idFromTitle } from "./schemaGenerators";
export * from "./fetch";
export * from "./users";
export * from "./schemaGenerators";
export * from "./editSchemaGenerators";

/**
 * @typedef {import("../store/WriteClass/reducer").WriteClass} WriteClass
 */

/**
 * Get page schema and form data for given page id
 * @param {string} page_id id for page you want to get schema of
 * @param {WriteClass} write_class a WriteClass state section
 * @returns {[any, any]} [page_schema, page_form]
 */
export function getPageSchema(page_id, write_class) {
    const { schema } = write_class;

    if (
        schema &&
        schema.hasOwnProperty("properties") &&
        schema.properties.hasOwnProperty(page_id)
    ) {
        return [
            schema.properties[page_id],
            get(write_class, ["form", page_id], {}),
        ];
    } else {
        return [undefined, {}];
    }
}

export function isResultClassesPage(property) {
    return (
        property.hasOwnProperty("type") && property.type === "result_classes"
    );
}

/**
 * Check if property is a results page
 * @param {Object} property
 * @returns {boolean}
 */
export function isResultPage(property) {
    return get(property, ["type"], "") === "result";
}

/**
 * Gets the page schema for the current WriteClass state
 * @param {WriteClass} write_class a WriteClass state section
 * @returns {[any, any]} [page_schema, page_form]
 */
export function getCurrentPageSchema(write_class) {
    const { current_page_id } = write_class;

    return getPageSchema(current_page_id, write_class);
}

function strip_html(html) {
    var tmp = document.implementation.createHTMLDocument("New").body;
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
}

/**
 * @typedef PageIsValidConfig
 * @type {object}
 * @property {WriteClass} write_class
 * @property {object} schema
 */

/**
 * Checks if current page is valid to be submitted. Currently
 * the `required` section of the schema is good enough
 * @param {PageIsValidConfig} options
 * @returns {boolean}
 */
export function pageIsValid({ write_class }) {
    // The 'required' array is enough to validate pages for now, but we may need
    // more complex logic in the future
    // get current page

    const [page, form] = getCurrentPageSchema(write_class);

    let is_valid = true;
    for (const property in form) {
        const value = form[property];
        const input_schema = get(page, ["properties", property]);
        const required = (page.required || []).indexOf(property) > -1;
        if (!required) {
            continue;
        }
        if (input_schema === undefined || input_schema.type === undefined) {
            continue;
        }
        switch (input_schema.type) {
            case "string":
                if (input_schema["X-Format"] === "richtext") {
                    if (value === undefined) {
                        is_valid = false;
                        break;
                    }
                    const decoded = b64DecodeUnicode(value);
                    const stripped = strip_html(decoded);
                    if (stripped.length === 0) {
                        is_valid = false;
                    }
                }
                break;
            default:
                break;
        }
    }

    return is_valid;
}

/**
 * Checks if rule is valid
 * @param {*} rule
 * @returns {boolean}
 */
export function isValidRule(rule) {
    if (!isArray(rule) || rule.length < 2) {
        return false;
    }

    return true;
}

/**
 * Evaluates a rule in the context of the WriteClass state
 * @param {*} rule a Rule type @see isValidRule
 * @param {*} write_class a WriteClass state section @see Store#WriteClass
 * @returns {string|undefined} if rule passes, returns the resulting path, else returns `undefined`
 */
export function evalRule(rule, write_class) {
    if (isValidRule(rule)) {
        const [bool_path, result] = rule;

        const bool_result = calculateBool(bool_path, write_class);

        if (bool_result === true) {
            if (isString(result)) {
                return result;
            } else if (isValidRule(result)) {
                return evalRule(result, write_class);
            } else {
                console.warn("utils::evalRule -- has bad result", result);
                return undefined;
            }
        } else {
            return undefined;
        }
    } else {
        console.error("utils::evalRule -- could not evaluate rule:", rule);
        return undefined;
    }
}

export function reduceRules(rules, write_class, default_next_page) {
    let has_set_rule = false;
    return rules.reduce((prev, rule) => {
        if (!isValidRule(rule)) {
            return prev;
        }

        const next_page = evalRule(rule, write_class);

        console.log(`RULE: ${JSON.stringify(rule)}, ${next_page}`);
        if (!has_set_rule && isString(next_page)) {
            has_set_rule = true;
            return next_page;
        } else {
            return prev;
        }
    }, default_next_page);
}

export function getBackPageId(write_class) {
    const { schema, submitted_pages, current_page_id } = write_class;

    const rules = get(
        schema,
        ["properties", current_page_id, "X-Go-Back-Rules"],
        []
    );

    return reduceRules(
        rules,
        write_class,
        submitted_pages[submitted_pages.length - 1]
    );
}

/**
 * getNextPageId options definition
 * @typedef {Object} GetNextPageIdConfig
 * @property {WriteClass} write_class
 * @property {object} form
 */

/**
 *
 * @param {GetNextPageIdConfig} options
 * @returns {string} next page id
 */
export function getNextPageId(options) {
    const { submitted_pages, write_class } = options;

    const { schema } = write_class;

    const prev_page =
        submitted_pages.length > 0
            ? submitted_pages[submitted_pages.length - 1]
            : undefined;
    if (prev_page === undefined) {
        console.error("utils::getNextPageId did not have previous page");
        return "badPage";
    }

    const order = schema["X-Order"];
    const previous_page_order_index = order.indexOf(prev_page);
    if (previous_page_order_index === undefined) {
        console.error(
            `utils::getNextPageId could not find previous page: ${prev_page}\n in order: ${order}`
        );
        return "badPage";
    }

    let default_next_page = undefined;
    if (previous_page_order_index < order.length - 1) {
        default_next_page = order[previous_page_order_index + 1];
    }

    let prev_page_schema = get(schema, ["properties", prev_page], {});
    if (prev_page_schema.type === "results") {
        prev_page_schema = getResultsFromConfig(prev_page_schema, write_class);
    }

    const rules = get(prev_page_schema, ["X-Rules"], []);

    const reduced_rules = reduceRules(rules, write_class, default_next_page);
    if (reduced_rules === undefined) {
        console.error("utils::getNextPageId could not find a next page");
    }

    let next_page_id = reduced_rules;
    // check if `next_page_id` is a page id or a path and get the page schema
    let next_page_property = get(schema, ["properties", next_page_id]);
    if (next_page_property === undefined) {
        next_page_property = evalPath(next_page_id, write_class);
        if (next_page_property === undefined) {
            throw new Error(`Could not get page property for: ${next_page_id}`);
        }
        next_page_id = next_page_property.id;
    }

    return { next_page_id, next_page_property };
}

export function isCalculated(result) {
    return isObject(result) && result.type === "calculated";
}

export function isCalculatedMap(result) {
    return (
        isCalculated(result) &&
        get(result, ["X-Method"], "") === "map" &&
        result.hasOwnProperty("X-Source") &&
        result.hasOwnProperty("X-Score-Map") &&
        isArray(result["X-Score-Map"])
    );
}

export function isCalculatedSum(result) {
    return isCalculated(result) && get(result, ["X-Method"], "") === "sum";
}

export function isPathType(path_type) {
    return isObject(path_type) && isString(path_type["X-Source"]);
}

/**
 * Evaluate path with `form` and `schema` in context
 * @param {string} path path to evaluate
 * @param {WriteClass} write_class
 * @return {any}
 */
export function evalPath(path, write_class) {
    /* eslint-disable no-unused-vars */
    const { form, schema } = write_class;
    /* eslint-enable no-unused-vars */

    try {
        /* eslint-disable no-eval */
        return eval(path);
        /* eslint-enable no-eval */
    } catch (e) {
        //console.warn('utils::evalPath error:', e);
        return undefined;
    }
}

export function calculateSum(sum_property, write_class) {
    const sources = get(sum_property, ["X-Sources"], []);
    // recursively sum sources
    return sum(
        sources.map((source) => {
            return calculatePath({
                calculation_path: source,
                write_class,
            });
        })
    );
}

/**
 * @typedef {Object} CalculatePathOptions
 * @property {string} calculation_path path to calculate
 * @property {WriteClass} write_class
 */

/**
 * Main function for calculating path data.
 * This can calculate `map`, `sum`, `calculated_bool`, and `string` types
 * @param {CalculatePathOptions} options
 * @param {number|boolean} def default if getting path fails
 */
export function calculatePath(options, def = 0) {
    const { calculation_path, write_class } = options;

    const eval_result = evalPath(calculation_path, write_class);
    if (eval_result === undefined) {
        return def;
    }

    if (isCalculatedMap(eval_result)) {
        // calculate more
        const source = get(eval_result, ["X-Source"]);
        const source_value = calculatePath(
            {
                calculation_path: source,
                write_class,
            },
            null
        );
        const score_map = new Map(get(eval_result, ["X-Score-Map"], []));
        if (score_map.has(source_value) || score_map.has("default")) {
            const value = score_map.has(source_value)
                ? score_map.get(source_value)
                : score_map.get("default");

            if (isString(value)) {
                return calculatePath(
                    {
                        calculation_path: value,
                        write_class,
                    },
                    def
                );
            } else if (isNumber(value)) {
                return value;
            } else {
                console.error(
                    "utils::calculatePath calculated map value is not number or string",
                    value
                );
            }
        } else {
            console.warn("utils::calculatePath could not get score");
            return 0;
        }
    } else if (isCalculatedSum(eval_result)) {
        return calculateSum(eval_result, write_class);
    } else if (isNumber(eval_result) || isBoolean(eval_result)) {
        return eval_result;
    } else if (isCalculatedBool(eval_result)) {
        return calculateBool(calculation_path, write_class);
    } else if (isString(eval_result)) {
        return eval_result;
    } else {
        console.error(
            "utils::calculatePath was given bad path",
            calculation_path
        );
    }
}

export function isCalculatedBool(calculated_bool) {
    return (
        isObject(calculated_bool) &&
        get(calculated_bool, ["type"], "") === "calculated_bool" &&
        isString(calculated_bool["X-Method"]) &&
        isArray(calculated_bool["X-Args"])
    );
}

export function isGE(calculated_bool) {
    return (
        isCalculatedBool(calculated_bool) &&
        calculated_bool["X-Method"] === ">=" &&
        calculated_bool["X-Args"].length >= 2
    );
}

export function isLE(calculated_bool) {
    return (
        isCalculatedBool(calculated_bool) &&
        calculated_bool["X-Method"] === "<=" &&
        calculated_bool["X-Args"].length >= 2
    );
}

export function isOR(calculated_bool) {
    return (
        isCalculatedBool(calculated_bool) &&
        calculated_bool["X-Method"] === "||" &&
        calculated_bool["X-Args"].length >= 1
    );
}

export function isAND(calculated_bool) {
    return (
        isCalculatedBool(calculated_bool) &&
        calculated_bool["X-Method"] === "&&" &&
        calculated_bool["X-Args"].length >= 1
    );
}

export function isEQ(calculated_bool) {
    return (
        isCalculatedBool(calculated_bool) &&
        calculated_bool["X-Method"] === "==" &&
        calculated_bool["X-Args"].length >= 1
    );
}

export function isNE(calculated_bool) {
    return (
        isCalculatedBool(calculated_bool) &&
        calculated_bool["X-Method"] === "!=" &&
        calculated_bool["X-Args"].length >= 1
    );
}

/**
 * In a calculated_bool type, get the value for the argument
 * @param {string|number|boolean} arg Argument to get value for
 * @param {WriteClass} write_class
 * @param {number|boolean} def default
 * @returns {number|boolean}
 */
export function getValueFromArg(arg, write_class, def = 0) {
    if (isString(arg)) {
        return calculatePath(
            {
                calculation_path: arg,
                write_class,
            },
            def
        );
    } else if (isNumber(arg)) {
        return arg;
    } else if (isBoolean(arg)) {
        return arg;
    } else {
        console.warn("utils::getValueFromArg got bad arg:", arg);
        return def;
    }
}

/**
 * Given a path, calculate the value
 * @param {string} calculated_bool_path path to calculated bool
 * @param {WriteClass} write_class
 * @param {any} def default return value
 */
export function calculateBool(calculated_bool_path, write_class, def = false) {
    let evaluated_path = evalPath(calculated_bool_path, write_class);

    if (evaluated_path === undefined) {
        evaluated_path = def;
    }

    if (isGE(evaluated_path)) {
        const [left, right] = evaluated_path["X-Args"];

        let left_val = getValueFromArg(left, write_class, null);
        let right_val = getValueFromArg(right, write_class, null);
        if (left_val === null || right_val === null) {
            return false;
        }

        return left_val >= right_val;
    } else if (isLE(evaluated_path)) {
        const [left, right] = evaluated_path["X-Args"];

        let left_val = getValueFromArg(left, write_class, null);
        let right_val = getValueFromArg(right, write_class, null);
        if (left_val === null || right_val === null) {
            return false;
        }

        return left_val <= right_val;
    } else if (isEQ(evaluated_path)) {
        const [left, right] = evaluated_path["X-Args"];

        let left_val = getValueFromArg(left, write_class, null);
        let right_val = getValueFromArg(right, write_class, null);
        if (left_val === null || right_val === null) {
            return false;
        }

        return left_val === right_val;
    } else if (isNE(evaluated_path)) {
        const [left, right] = evaluated_path["X-Args"];

        let left_val = getValueFromArg(left, write_class, null);
        let right_val = getValueFromArg(right, write_class, null);
        if (left_val === null || right_val === null) {
            return false;
        }

        return left_val !== right_val;
    } else if (isAND(evaluated_path)) {
        const args = evaluated_path["X-Args"];

        return args.reduce((prev, curr) => {
            if (prev === false) {
                return false;
            } else {
                return calculateBool(curr, write_class);
            }
        }, true);
    } else if (isOR(evaluated_path)) {
        const args = evaluated_path["X-Args"];

        return args.reduce((prev, curr) => {
            if (prev === true) {
                return true;
            } else {
                return calculateBool(curr, write_class);
            }
        }, false);
    } else if (isBoolean(evaluated_path)) {
        return evaluated_path;
    } else {
        console.warn(
            "utils::calculateBool evaluated an unknown path type:",
            evaluated_path
        );
        return false;
    }
}

/**
 * Return schema for result given a result page config
 * @param {any} results_config results page schema
 * @param {WriteClass} write_class
 */
export function getResultsFromConfig(results_config, write_class) {
    const result_map = get(results_config, ["X-Result-Map"], []);

    const results = result_map.reduce((prev, curr) => {
        if (prev !== undefined) {
            return prev;
        } else if (isArray(curr) && curr.length >= 2) {
            const [calculation_path, results_path] = curr;

            const bool_result = calculateBool(calculation_path, write_class);

            if (bool_result === true) {
                return cloneDeep(evalPath(results_path, write_class));
            }
            return prev;
        }

        return prev;
    }, undefined);

    return results;
}

export function getResults(write_class) {
    const { schema, current_page_id } = write_class;

    const results_config = get(schema, ["properties", current_page_id]);

    if (results_config === undefined || results_config.type !== "results") {
        console.error(
            "utils::getResults could not get results config for page:",
            current_page_id
        );
        return;
    }

    let results = getResultsFromConfig(results_config, write_class);

    // results['X-Classes'] = get(results, ['X-Classes'], []).map((class_path) => {
    //     return evalPath(class_path, write_class);
    // });

    //results['X-Rules'] = get(results, ['X-Rules'], [])

    return results;
}

/**
 * Schema properties can be reusable by using the `path` type
 * This function will take a path type and return the path it refers to
 * @param {any} property_path_type path to property
 * @param {WriteClass} write_class
 * @returns {any} property at path
 */
export function getPropertyFromPathType(property_path_type, write_class) {
    if (!isPathType(property_path_type)) {
        console.error(
            "utils::getPropertyFromPathType -- property_path_type not of correct type:",
            property_path_type
        );
        return {};
    }
    let property = evalPath(property_path_type["X-Source"], write_class);
    console.log(`PROPERTY PATH TYPE: ${JSON.stringify(property_path_type)}`);
    property["X-Original-Path"] = property_path_type["X-Source"];
    return property;
}

/**
 * Check if id refers to a results page
 * @param {string} page_id
 * @param {WriteClass} write_class
 * @returns {boolean}
 */
export function isResultsPage(page_id, write_class) {
    const page_type = get(
        write_class,
        ["schema", "properties", page_id, "type"],
        ""
    );

    return page_type === "results";
}

/**
 * Look for `final_result_id` in the schema and get the final result
 * @param {WriteClass} write_class
 */
export function getFinalResult(write_class) {
    const final_result_config = get(write_class, [
        "schema",
        "properties",
        "final_result_id",
    ]);
    if (isUndefined(final_result_config)) {
        return undefined;
    } else {
        // get first option returned. Should be an array of only one option
        return getResultsFromConfig(final_result_config, write_class);
    }
}

/**
 * Page properties/components can be conditionally visible,
 * check if it should be shown
 * @param {any} component schema for component property
 * @param {WriteClass} write_class
 */
export function isComponentVisible(component, write_class) {
    const only_if_path = get(component, ["X-Only-If"]);

    if (only_if_path === undefined) {
        return true;
    } else {
        return calculateBool(only_if_path, write_class);
    }
}

export function getDefaultValueForComponent(component) {
    const d = get(component, ["default"], undefined);

    return d;
}

export function getMarkup(content) {
    return {
        __html: content,
    };
}

export function searchResponsesForUser(search_string, filters) {
    const uri =
        "/response?" +
        qs.stringify({
            user_search: search_string,
            ...filters,
        });

    return fetchDataPromise(uri, {
        method: "GET",
    }).then(
        (res) => {
            return res.json();
        },
        (err) => {
            console.log("utils::searchUsers -- error:", err);
        }
    );
}

export function deleteFile(file_id) {
    if (file_id === undefined) {
        return;
    }

    return fetchDataPromise("/upload/" + file_id, {
        method: "DELETE",
    }).then((res) => {
        console.log("Successfully deleted file with id:", file_id);
        return res;
    });
}

const BASE_64_PREFIX = "base64:";

export function b64EncodeUnicode(str) {
    // first we use encodeURIComponent to get percent-encoded UTF-8,
    // then we convert the percent encodings into raw bytes which
    // can be fed into btoa.
    return (
        BASE_64_PREFIX +
        btoa(
            encodeURIComponent(str).replace(
                /%([0-9A-F]{2})/g,
                function toSolidBytes(match, p1) {
                    return String.fromCharCode("0x" + p1);
                }
            )
        )
    );
}

export function b64DecodeUnicode(str) {
    // Going backwards: from bytestream, to percent-encoding, to original string.
    if (str.indexOf(BASE_64_PREFIX) === 0) {
        str = str.substring(BASE_64_PREFIX.length);
    }
    let decoded = decodeURIComponent(
        atob(str)
            .split("")
            .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
    );

    return decoded;
}

export function uploadFile(file) {
    let formData = new FormData();

    formData.append("file", file);

    return fetch(process.env.REACT_APP_API_URL + "/upload/", {
        method: "POST",
        body: formData,
        credentials: "include",
        mode: "cors",
    });
}

export function orderProperties(schema, write_class) {
    var property_order = get(schema, ["X-Order"], []);

    var filtered_properties = {};
    property_order.forEach((key) => {
        const property = get(schema, ["properties", key]);
        if (isUndefined(property)) {
            console.error(
                `pageSet::render -- no such property '${key}' in:`,
                schema
            );
        }
        if (isComponentVisible(property, write_class)) {
            filtered_properties[key] = schema.properties[key];
        } else {
            // remove required
            schema.required = get(schema, "required", []).filter((item) => {
                return item !== key;
            });
            schema["X-Order"] = schema["X-Order"].filter((item) => {
                return item !== key;
            });
        }
    });

    return filtered_properties;
}

/**
 * When editing a schema page we need to switch out the `path`
 * types
 * @param {*} schema
 * @param {string} page_id
 */
export function prepareSchemaPageForEditing(schema, page_id) {
    // set page schema

    let page_schema = cloneDeep(get(schema, ["properties", page_id]));
    if (page_schema === undefined) {
        return;
    }

    let path_types = {};
    // some properties are paths to reusable properties, we need to eval them
    forOwn(page_schema.properties, (value, key) => {
        if (value.type === "path") {
            page_schema.properties[key] = getPropertyFromPathType(value, {
                schema,
            });
            path_types[key] = value;
        }
    });

    return [page_schema, path_types];
}

export const isPageTitleValid = (schema, title) => {
    const id = idFromTitle(title);
    const pages = get(schema, "X-Order", []);
    return !includes(pages, id);
};

export const validateJSON = (json_string) => {
    try {
        const o = JSON.parse(json_string);

        // Handle non-exception-throwing cases:
        // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
        // but... JSON.parse(null) returns null, and typeof null === "object",
        // so we must check for that, too
        if (o && typeof o === "object") {
            return o;
        }
    } catch (e) {
        console.error("Invalid JSON", e.message);
    }
    return false;
};



// WEBPACK FOOTER //
// ./src/utils/index.js